import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../environments/environment';
import { routeInfo } from '../activity/route-template';

/** Tells the backend which screen a call was made from; the request log keeps it for filtering. */
export const SCREEN_HEADER = 'X-Screen-Route';

@Injectable()
export class TraceInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const traceId = uuidv4();

    const headers: Record<string, string> = {
      // X-Request-ID is the one correlation id every backend log, audit row and gRPC/Kafka hop carries.
      // X-Trace-ID is kept, with the same value, for readers that still use the old name.
      'X-Request-ID': traceId,
      'X-Trace-ID': traceId,
    };
    // Only our own API learns the screen, and only as a route template (`/hr/employee/:id`),
    // never the concrete URL, so ids stay out of the logs and third-party hosts get nothing extra.
    const screen = this.isOwnApi(req.url) ? this.screenTemplate() : '';
    if (screen) {
      headers[SCREEN_HEADER] = screen;
    }

    const cloned = req.clone({ setHeaders: headers });

    if (!environment.production) {
      console.debug(`[HTTP] ${req.method} ${req.url} | trace_id=${traceId}`);
    }

    return next.handle(cloned);
  }

  private screenTemplate(): string {
    try {
      const { template } = routeInfo(this.router.routerState.snapshot.root);
      return template === '/' ? '' : template;
    } catch {
      return '';
    }
  }

  /** Relative URLs and URLs under the configured API base are ours. */
  private isOwnApi(url: string): boolean {
    const base = environment.apiBaseUrl;
    return url.startsWith(base) || url.startsWith('/') || !/^[a-z][a-z0-9+.-]*:\/\//i.test(url);
  }
}
